/**
 * Widget Configuration Parser
 * Parses URL query parameters to configure the widget for iframe embedding
 */

/**
 * Parse widget configuration from URL query parameters
 * @returns {Object} Configuration object with all widget settings
 */
export function parseWidgetConfig() {
  const params = new URLSearchParams(window.location.search);

  return {
    // Default currency/crypto settings
    defaultFiat: params.get('fiat') || 'USD',
    defaultCrypto: params.get('crypto') || 'USDC',
    defaultNetwork: params.get('network') || 'arbitrum',
    defaultAmount: params.get('amount') || '',

    // Network filter: when network is explicitly passed in URL, only show tokens for that network
    networkFilter: params.has('network') ? params.get('network') : null,

    // Pre-filled wallet address
    walletAddress: params.get('wallet') || '',

    // Theming
    primaryColor: params.get('primaryColor') || '#7c3aed',
    backgroundColor: params.get('bgColor') || '#ffffff',
    borderRadius: params.get('borderRadius') || '16',

    // UI options
    hideHeader: params.get('hideHeader') === 'true',
    hideFooter: params.get('hideFooter') === 'true',

    // Provider restrictions
    allowedProviders: params.get('providers')?.split(',').filter(Boolean) || null,

    // Security
    parentOrigin: params.get('origin') || '*',

    // Embedding mode flag
    isEmbedded: params.get('embed') === 'true',

    // Merchant identification
    merchantId: params.get('merchantId') || null,
    orderId: params.get('orderId') || null,

    // Callback URLs
    successUrl: params.get('successUrl') || null,
    failureUrl: params.get('failureUrl') || null,

    // Pre-selected payment options
    paymentMethod: params.get('paymentMethod') || null,
    provider: params.get('provider') || null,

    // Field locking (for exchange/wallet iframe integration)
    lockWallet: params.get('lockWallet') === 'true',
    lockAmount: params.get('lockAmount') === 'true',
    lockFiat: params.get('lockFiat') === 'true',
    lockCrypto: params.get('lockCrypto') === 'true',

    // Theme (dark/light) - defaults to dark
    theme: params.get('theme') || 'dark',

    // Widget mode: 'default' (normal widget) | 'wallet' (wallet app integration)
    mode: params.get('mode') || 'default',
  };
}

/**
 * Apply theme configuration to CSS custom properties
 * @param {Object} config - Widget configuration object
 */
export function applyTheme(config) {
  const root = document.documentElement;

  if (config.primaryColor) {
    root.style.setProperty('--primary-color', config.primaryColor);
    // Generate a darker hover color
    const hoverColor = adjustColorBrightness(config.primaryColor, -20);
    root.style.setProperty('--primary-hover', hoverColor);
  }

  if (config.backgroundColor) {
    root.style.setProperty('--widget-bg', config.backgroundColor);
  }

  if (config.borderRadius) {
    root.style.setProperty('--border-radius', `${config.borderRadius}px`);
  }
}

/**
 * Adjust color brightness
 * @param {string} hex - Hex color string
 * @param {number} amount - Amount to adjust (-255 to 255)
 * @returns {string} Adjusted hex color
 */
function adjustColorBrightness(hex, amount) {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse RGB values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Adjust brightness
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Validate that the parent origin is allowed
 * @param {string} allowedOrigin - Configured allowed origin
 * @param {string} actualOrigin - Actual parent origin from event
 * @returns {boolean} Whether the origin is allowed
 */
export function validateOrigin(allowedOrigin, actualOrigin) {
  if (allowedOrigin === '*') return true;
  if (!actualOrigin) return false;

  // Support comma-separated list of allowed origins
  const allowedOrigins = allowedOrigin.split(',').map(o => o.trim());
  return allowedOrigins.includes(actualOrigin);
}

export default {
  parseWidgetConfig,
  applyTheme,
  validateOrigin,
};
